import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import {
	Table,
	TableBody,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
	PaginationEllipsis,
} from "@/components/ui/pagination";
import { Users, Plus, Edit3, Mail } from "lucide-react";
import { UserTableRow } from "./components/user-group-assignment/userTableRow.component";
import { SearchFilterBar } from "./components/user-group-assignment/searchFilterBar.component";
import { BulkGroupAssignment } from "./components/user-group-assignment/bulkGroupAssignment.component";
import EmailInvitation from "@/features/orgaAdmin/adminOverview/components/user-invitation/emailInvitation.component";
import { OrgaAdminService } from "@/lib/orgaAdmin/orgaAdmin.service";
import type {
	OrganizationMemberDTO,
	GruppeDTO,
} from "@/lib/orgaAdmin/orgaAdmin.types";
import { useAuth } from "@/lib/contexts/auth.context";
import type { OrgaMemberRole, OrganizationRole } from "@/lib/auth/auth.types";
import OrgaAdminGruppeService from "@/lib/orgaAdmin/orgaAdminGruppe.service";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import GroupManagementTab from "./components/group-management/groupManagementTab.component";
import {
	UserGroupRemovalGuardProvider,
	useUserGroupRemovalGuard,
} from "./contexts/userGroupRemovalGuard.context";

/**
 * UserGroupManagement page
 *
 * This page is used to manage the status of users in an organization.
 * It allows you to invite users to the organization, assign them to groups, and manage the groups.
 *
 * It also allows you to bulk edit the groups of multiple users at once.
 *
 * @returns The UserGroupManagement page
 */
function UserGroupManagementContent() {
	const { currentOrganization, user } = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();
	const [members, setMembers] = useState<OrganizationMemberDTO[]>([]);
	const [gruppen, setGruppen] = useState<GruppeDTO[]>([]);
	const [userIdToGruppeIds, setUserIdToGruppeIds] = useState<
		Record<string, string[]>
	>({});
	const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
	const [filterGruppeIds, setFilterGruppeIds] = useState<string[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [showEmailInvitation, setShowEmailInvitation] = useState(false);
	const [showBulkEditor, setShowBulkEditor] = useState(false);
	// deprecated local creation state (moved to GroupManagementTab)
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const hasAppliedUrlPreselectRef = useRef(false);

	// Use context for destructive confirmation + loading
	const {
		confirmIfHasAssignedKorrekturSteps,
		confirmIfAnySelectedHasAssignedKorrekturSteps,
	} = useUserGroupRemovalGuard();

	const handleSearchChange = (value: string) => {
		setSearchTerm(value);
		setCurrentPage(1);
	};

	const handleFilterGroupsChange = (groupIds: string[]) => {
		setFilterGruppeIds(groupIds);
		setCurrentPage(1);
	};

	const reloadData = useCallback(async () => {
		if (!currentOrganization) return;
		const orgId = currentOrganization.organizationId;
		const [fetchedGruppen, fetchedMembers] = await Promise.all([
			OrgaAdminGruppeService.getInstance().getGruppenInOrganization(orgId),
			OrgaAdminService.getInstance().getUsersInOrganization(orgId),
		]);
		// Convert OrgaAdminGruppeDTO to GruppeDTO to match the expected type
		const convertedGruppen: GruppeDTO[] = fetchedGruppen.map((gruppe) => ({
			id: gruppe.id,
			title: gruppe.title,
			organizationId: gruppe.organizationId,
			studentKlausuren: gruppe.studentKlausuren.map((sk) => ({
				id: sk.id,
				title: sk.internalIdentifier,
			})),
			users: gruppe.users.map((user) => ({
				id: user.id,
				name: user.name,
				email: user.email,
			})),
			klausuren: Array.isArray(gruppe.klausuren)
				? gruppe.klausuren.map((k: unknown) => {
						const klausur = k as Record<string, unknown>;
						return {
							id: (klausur.id as string) || "",
							title: (klausur.title as string) || "",
						};
					})
				: [],
		}));
		setGruppen(convertedGruppen);
		setMembers(fetchedMembers);
		const map: Record<string, string[]> = {};
		for (const m of fetchedMembers) {
			map[m.id] = (m.gruppen ?? []).map((g) => g.id);
		}
		setUserIdToGruppeIds(map);
	}, [currentOrganization]);

	useEffect(() => {
		reloadData();
	}, [reloadData]);

	// Effect to handle URL parameter groupId preselection (apply once)
	useEffect(() => {
		if (hasAppliedUrlPreselectRef.current) return;
		const groupIdFromUrl = searchParams.get("groupId");
		if (groupIdFromUrl && gruppen.length > 0) {
			const groupExists = gruppen.some((group) => group.id === groupIdFromUrl);
			if (groupExists) {
				setFilterGruppeIds([groupIdFromUrl]);
				hasAppliedUrlPreselectRef.current = true;
			}
		}
	}, [searchParams, gruppen]);

	const handleSingleEmailInvite = async (email: string) => {
		if (!currentOrganization) {
			throw new Error("No organization found");
		}
		await OrgaAdminService.getInstance().inviteUserToOrganization(
			currentOrganization.organizationId,
			email,
		);
		await reloadData();
		toast.success("Einladung erfolgreich gesendet");
		setShowEmailInvitation(false);
	};

	/**
	 * Handle the change of the groups of a user
	 * @param userId
	 * @param nextIds The new group IDs
	 * @returns The void
	 */
	const handleRowGroupsChange = async (userId: string, nextIds: string[]) => {
		if (!currentOrganization) return;
		const orgId = currentOrganization.organizationId;
		const currentIds = new Set(userIdToGruppeIds[userId] ?? []);
		const nextIdSet = new Set(nextIds);
		try {
			// Determine removals and additions
			const gruppenToRemove = Array.from(currentIds).filter(
				(gid) => !nextIdSet.has(gid),
			);
			const gruppenToAdd = Array.from(nextIdSet).filter(
				(gid) => !currentIds.has(gid),
			);

			// For single-user removal(s), ensure confirmation if there are assigned korrektur steps
			for (const gid of gruppenToRemove) {
				const gruppeTitle =
					gruppen.find((g) => g.id === gid)?.title ?? "dieser Gruppe";
				const confirmed = await confirmIfHasAssignedKorrekturSteps(
					orgId,
					gid,
					userId,
					gruppeTitle,
				);
				if (!confirmed) return;
			}

			// Execute removals
			for (const gid of gruppenToRemove) {
				await OrgaAdminGruppeService.getInstance().unassignUserFromGruppe(
					orgId,
					gid,
					userId,
				);
			}

			// Execute additions
			for (const gid of gruppenToAdd) {
				await OrgaAdminGruppeService.getInstance().assignUserToGruppe(
					orgId,
					gid,
					userId,
				);
			}
			setUserIdToGruppeIds((prev) => ({ ...prev, [userId]: [...nextIdSet] }));
			setMembers((prev) =>
				prev.map((m) =>
					m.id === userId
						? {
								...m,
								gruppen: gruppen
									.filter((g) => nextIdSet.has(g.id))
									.map((g) => ({
										id: g.id,
										title: g.title,
										organizationId: g.organizationId,
									})),
							}
						: m,
				),
			);
		} catch (e) {
			console.error(e);
		}
	};

	const handleRolesChange = async (
		userId: string,
		newRoles: OrgaMemberRole[],
	) => {
		if (!currentOrganization) return;

		// Validate that at least one role is selected
		if (newRoles.length === 0) {
			toast.error("Ein Benutzer muss mindestens eine Rolle haben");
			return;
		}

		const orgId = currentOrganization.organizationId;
		const orgaAdminService = OrgaAdminService.getInstance();

		try {
			const response = await orgaAdminService.updateUserRoles(
				orgId,
				userId,
				newRoles,
			);

			// Update the local state to reflect the role changes
			setMembers((prev) =>
				prev.map((m) =>
					m.id === userId
						? {
								...m,
								// Convert OrgaMemberRole to OrganizationRole for the DTO
								orgaRoles: response.orgaRoles.map(
									(role) => role as unknown as OrganizationRole,
								),
							}
						: m,
				),
			);

			toast.success("Rollen erfolgreich aktualisiert");
		} catch (error) {
			console.error("Error updating user roles:", error);

			// Check if this is the specific validation error about orgaRoles
			if (
				error instanceof Error &&
				error.message.includes("orgaRoles must NOT have fewer than 1 items")
			) {
				toast.error("Ein Benutzer muss mindestens eine Rolle haben");
			} else {
				toast.error("Fehler beim Aktualisieren der Rollen");
			}
		}
	};

	const displayedMembers = members.filter((m) => {
		const passesGroup =
			filterGruppeIds.length === 0
				? true
				: (() => {
						const ids =
							userIdToGruppeIds[m.id] ?? (m.gruppen ?? []).map((g) => g.id);
						return filterGruppeIds.some((fid) => ids.includes(fid));
					})();
		if (!passesGroup) return false;
		const q = searchTerm.trim().toLowerCase();
		if (q.length === 0) return true;
		const name = (m.name ?? "").toLowerCase();
		const email = (m.email ?? "").toLowerCase();
		return name.includes(q) || email.includes(q);
	});

	// Pagination logic
	const totalPages = Math.ceil(displayedMembers.length / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const paginatedMembers = displayedMembers.slice(startIndex, endIndex);

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handleItemsPerPageChange = (value: string) => {
		setItemsPerPage(Number(value));
		setCurrentPage(1);
	};

	const areAllSelected =
		paginatedMembers.length > 0 &&
		paginatedMembers.every((m) => selectedUserIds.includes(m.id));

	const toggleSelectAll = () => {
		const visibleIds = paginatedMembers.map((m) => m.id);
		const allVisibleSelected = visibleIds.every((id) =>
			selectedUserIds.includes(id),
		);
		if (allVisibleSelected) {
			setSelectedUserIds((prev) =>
				prev.filter((id) => !visibleIds.includes(id)),
			);
		} else {
			setSelectedUserIds((prev) =>
				Array.from(new Set([...prev, ...visibleIds])),
			);
		}
	};

	const toggleSelectUser = (userId: string) => {
		setSelectedUserIds((prev) =>
			prev.includes(userId)
				? prev.filter((id) => id !== userId)
				: [...prev, userId],
		);
	};

	/**
	 * Toggle the group for the selected users
	 * @param gruppeId The group ID to toggle
	 * @returns The void
	 */
	const toggleGroupForSelected = async (gruppeId: string) => {
		if (!currentOrganization) return;
		const orgId = currentOrganization.organizationId;
		const gruppeSvc = OrgaAdminGruppeService.getInstance();
		try {
			const total = selectedUserIds.length;
			const allHave =
				total > 0 &&
				selectedUserIds.every((uid) =>
					(userIdToGruppeIds[uid] ?? []).includes(gruppeId),
				);

			// Determine which users need to be added or removed from the gruppe
			const usersToAdd: string[] = [];
			const usersToRemove: string[] = [];

			for (const userId of selectedUserIds) {
				const userGruppeIds = new Set(userIdToGruppeIds[userId] ?? []);
				if (allHave) {
					// If all should have the gruppe, remove users who already have it
					if (userGruppeIds.has(gruppeId)) {
						usersToRemove.push(userId);
					}
				} else {
					// If not all should have the gruppe, add users who don't have it
					if (!userGruppeIds.has(gruppeId)) {
						usersToAdd.push(userId);
					}
				}
			}

			// If removing from the group, confirm if any have assigned korrektur steps
			if (usersToRemove.length > 0) {
				const gruppeTitle = gruppen.find((g) => g.id === gruppeId)?.title;
				const ok = await confirmIfAnySelectedHasAssignedKorrekturSteps(
					orgId,
					gruppeId,
					usersToRemove,
					gruppeTitle,
				);
				if (!ok) return;
			}

			// Execute bulk operations
			if (usersToAdd.length > 0) {
				await gruppeSvc.addUsersToGruppe(orgId, gruppeId, usersToAdd);
			}
			if (usersToRemove.length > 0) {
				await gruppeSvc.removeUsersFromGruppe(orgId, gruppeId, usersToRemove);
			}

			setUserIdToGruppeIds((prev) => {
				const copy: Record<string, string[]> = { ...prev };
				for (const uid of selectedUserIds) {
					const set = new Set(copy[uid] ?? []);
					if (allHave) set.delete(gruppeId);
					else set.add(gruppeId);
					copy[uid] = Array.from(set);
				}
				return copy;
			});

			setMembers((prev) =>
				prev.map((m) => {
					if (!selectedUserIds.includes(m.id)) return m;
					const newSet = new Set(userIdToGruppeIds[m.id] ?? []);
					if (allHave) newSet.delete(gruppeId);
					else newSet.add(gruppeId);
					return {
						...m,
						gruppen: gruppen
							.filter((g) => newSet.has(g.id))
							.map((g) => ({
								id: g.id,
								title: g.title,
								organizationId: g.organizationId,
							})),
					};
				}),
			);
		} catch (e) {
			console.error(e);
		}
	};

	return (
		<Tabs defaultValue="people" className="flex flex-col h-main-content">
			{/* Header Section */}
			<div className="px-6 pt-4">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-blue-100 rounded-lg">
							<Users className="h-6 w-6 text-blue-600" />
						</div>
						<TabsList className="w-full bg-primary/10 white">
							<TabsTrigger value="people">Personen verwalten</TabsTrigger>
							<TabsTrigger value="groups">Gruppen verwalten</TabsTrigger>
						</TabsList>
					</div>
				</div>
			</div>
			<TabsContent value="people" className="flex-1 flex flex-col min-h-0">
				<div className="flex flex-col bg-white rounded-lg p-4 m-4 border flex-1 min-h-0">
					{/* Search and Filter Section */}
					<div className="flex items-center justify-between gap-2">
						<div className="flex-1 min-w-0">
							<SearchFilterBar
								searchTerm={searchTerm}
								onSearchChange={handleSearchChange}
								filterGroupIds={filterGruppeIds}
								onFilterGroupsChange={handleFilterGroupsChange}
								availableGroups={gruppen}
								selectedCount={selectedUserIds.length}
								onBulkEdit={() => setShowBulkEditor(true)}
								onClearFilters={() => {
									setFilterGruppeIds([]);
									setSearchTerm("");
									setCurrentPage(1);
									const next = new URLSearchParams(searchParams);
									next.delete("groupId");
									setSearchParams(next, { replace: true });
								}}
							/>
						</div>
						<div className="flex items-center gap-2">
							<Button
								onClick={() => setShowEmailInvitation(true)}
								className="flex items-center gap-2"
							>
								<Plus className="h-4 w-4" />
								Personen einladen
							</Button>
						</div>
					</div>

					{/* Table Section */}
					<div className="flex-1 overflow-y-auto mt-4">
						{/* Table */}
						<div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg min-h-0">
							<Table>
								<TableHeader className="sticky top-0 bg-white z-10 border-b border-gray-200">
									<TableRow className="bg-gray-50">
										<TableHead className="w-12 pl-6">
											<input
												type="checkbox"
												className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
												checked={areAllSelected}
												onChange={toggleSelectAll}
											/>
										</TableHead>
										<TableHead className="font-semibold text-gray-900 rounded-tl-lg">
											Person
										</TableHead>
										<TableHead className="font-semibold text-gray-900">
											Einladungsstatus
										</TableHead>
										<TableHead className="font-semibold text-gray-900">
											Rollen
										</TableHead>
										<TableHead className="font-semibold text-gray-900">
											Aktuelle Gruppen
										</TableHead>
										<TableHead className="font-semibold text-gray-900">
											Gruppen verwalten
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{paginatedMembers.map((m) => {
										const currentIds =
											userIdToGruppeIds[m.id] ??
											(m.gruppen ?? []).map((g) => g.id);
										const isCurrentUser = user?.email === m.email;
										return (
											<UserTableRow
												key={m.id}
												member={m}
												isSelected={selectedUserIds.includes(m.id)}
												currentGroupIds={currentIds}
												availableGroups={gruppen}
												organizationId={
													currentOrganization?.organizationId ?? ""
												}
												isCurrentUser={isCurrentUser}
												onSelectToggle={toggleSelectUser}
												onGroupsChange={handleRowGroupsChange}
												onRolesChange={handleRolesChange}
											/>
										);
									})}
									{paginatedMembers.length === 0 &&
										displayedMembers.length > 0 && (
											<tr>
												<td
													colSpan={6}
													className="text-center text-gray-500 py-8"
												>
													Keine Personen auf dieser Seite.
												</td>
											</tr>
										)}
								</TableBody>
							</Table>
						</div>
					</div>

					{/* Pagination Controls */}
					{displayedMembers.length > 0 && (
						<div className="mx-6 mt-2">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-2">
									<Select
										value={itemsPerPage.toString()}
										onValueChange={handleItemsPerPageChange}
									>
										<SelectTrigger className="w-20 h-6">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="5">5</SelectItem>
											<SelectItem value="10">10</SelectItem>
											<SelectItem value="20">20</SelectItem>
											<SelectItem value="50">50</SelectItem>
											<SelectItem value="100">100</SelectItem>
										</SelectContent>
									</Select>
									<span className="text-sm text-gray-600">
										/ {displayedMembers.length}{" "}
										{displayedMembers.length === 1 ? "Person" : "Personen"}
										{searchTerm || filterGruppeIds.length > 0
											? ` (gefiltert von ${members.length} gesamt)`
											: ""}
									</span>
								</div>

								{totalPages > 1 && (
									<Pagination>
										<PaginationContent>
											<PaginationItem>
												<PaginationPrevious
													href="#"
													onClick={(e) => {
														e.preventDefault();
														if (currentPage > 1) {
															handlePageChange(currentPage - 1);
														}
													}}
													className={
														currentPage <= 1
															? "pointer-events-none opacity-50"
															: ""
													}
												/>
											</PaginationItem>

											{/* First page */}
											{currentPage > 3 && (
												<>
													<PaginationItem>
														<PaginationLink
															href="#"
															onClick={(e) => {
																e.preventDefault();
																handlePageChange(1);
															}}
														>
															1
														</PaginationLink>
													</PaginationItem>
													{currentPage > 4 && (
														<PaginationItem>
															<PaginationEllipsis />
														</PaginationItem>
													)}
												</>
											)}

											{/* Pages around current */}
											{Array.from({ length: totalPages }, (_, i) => i + 1)
												.filter(
													(page) =>
														page >= Math.max(1, currentPage - 2) &&
														page <= Math.min(totalPages, currentPage + 2),
												)
												.map((page) => (
													<PaginationItem key={page}>
														<PaginationLink
															href="#"
															isActive={page === currentPage}
															onClick={(e) => {
																e.preventDefault();
																handlePageChange(page);
															}}
														>
															{page}
														</PaginationLink>
													</PaginationItem>
												))}

											{/* Last page */}
											{currentPage < totalPages - 2 && (
												<>
													{currentPage < totalPages - 3 && (
														<PaginationItem>
															<PaginationEllipsis />
														</PaginationItem>
													)}
													<PaginationItem>
														<PaginationLink
															href="#"
															onClick={(e) => {
																e.preventDefault();
																handlePageChange(totalPages);
															}}
														>
															{totalPages}
														</PaginationLink>
													</PaginationItem>
												</>
											)}

											<PaginationItem>
												<PaginationNext
													href="#"
													onClick={(e) => {
														e.preventDefault();
														if (currentPage < totalPages) {
															handlePageChange(currentPage + 1);
														}
													}}
													className={
														currentPage >= totalPages
															? "pointer-events-none opacity-50"
															: ""
													}
												/>
											</PaginationItem>
										</PaginationContent>
									</Pagination>
								)}
							</div>
						</div>
					)}
				</div>
			</TabsContent>

			{currentOrganization && (
				<TabsContent value="groups" className="flex-1 flex flex-col min-h-0">
					<GroupManagementTab
						organizationId={currentOrganization.organizationId}
						groups={gruppen}
						onReload={reloadData}
					/>
				</TabsContent>
			)}

			{/* Bulk Editor Sheet */}
			<Sheet
				open={!!showBulkEditor}
				onOpenChange={(open) => {
					if (!open) setShowBulkEditor(false);
				}}
			>
				<SheetContent side="right" className="w-full sm:max-w-lg">
					<SheetHeader className="border-b border-gray-200 pb-4">
						<SheetTitle className="flex items-center gap-2 text-xl">
							<Edit3 className="h-5 w-5 text-blue-600" />
							{selectedUserIds.length > 0
								? `${selectedUserIds.length} Personen bearbeiten`
								: "Gruppen zuweisen"}
						</SheetTitle>
						<SheetDescription className="text-gray-600">
							Gruppen für ausgewählte Personen verwalten
						</SheetDescription>
					</SheetHeader>

					<BulkGroupAssignment
						selectedMembers={members.filter((m) =>
							selectedUserIds.includes(m.id),
						)}
						availableGroups={gruppen}
						userGroupAssignments={userIdToGruppeIds}
						onToggleGroupForSelected={toggleGroupForSelected}
						onClose={() => setShowBulkEditor(false)}
					/>
				</SheetContent>
			</Sheet>

			{/* Email Invitation Sheet */}
			<Sheet
				open={showEmailInvitation}
				onOpenChange={(open) => {
					if (!open) setShowEmailInvitation(false);
				}}
			>
				<SheetContent side="right" className="w-full sm:max-w-2xl">
					<SheetHeader className="border-b border-gray-200 pb-4">
						<SheetTitle className="flex items-center gap-2 text-xl">
							<Mail className="h-5 w-5 text-blue-600" />
							Personen einladen
						</SheetTitle>
						<SheetDescription className="text-gray-600">
							Personen per E-Mail zur Plattform einladen
						</SheetDescription>
					</SheetHeader>
					<div className="mt-6">
						<EmailInvitation
							onSingleEmailInvite={handleSingleEmailInvite}
							onBulkEmailInvite={() => setShowEmailInvitation(false)}
							onClose={() => setShowEmailInvitation(false)}
						/>
					</div>
				</SheetContent>
			</Sheet>
		</Tabs>
	);
}

/**
 * UserGroupManagement page with the user group removal guard
 * @returns
 */
export default function UserGroupManagement() {
	return (
		<UserGroupRemovalGuardProvider>
			<UserGroupManagementContent />
		</UserGroupRemovalGuardProvider>
	);
}
